/**
 * PATH: packages/bills/src/components/StructuredBillRead.tsx
 * FILE_ID: BILLS-PKG-CMP-READ-001
 * PURPOSE: Read-only, clay/excel "what the AI read" view of a bill —
 *          supplier + bill meta + totals as labelled cells, and the
 *          line items as a spreadsheet grid. Cells are clickable and
 *          cross-highlight with the edit form (p1c).
 *
 * WHY: Operator (2026-06-20): "AI should read it in a way where I can see
 *      the read sections in an excel-like box that I can click to select…
 *      structured in the standard format". This is the standalone /
 *      split-centre read surface; it never mutates — editing stays in
 *      BillDataPanel, and clicking a read cell just focuses the matching
 *      form field.
 *
 * FLOW:
 *   ┌───────────┐   ┌──────────────────┐   ┌────────────────────────┐
 *   │ BillDetail │→ │ buildBillReadModel│→ │ sections + line grid     │
 *   └───────────┘   └──────────────────┘   │ (click → onFieldSelect)  │
 *                                          └────────────────────────┘
 * DEPENDENCIES:
 *   - billReadModel.ts: pure shaping + formatting.
 *   - billFieldKeys.ts: shared key vocabulary for cross-highlight.
 */

import type { BillDetail } from '../types';
import {
  buildBillReadModel,
  type ReadCell,
  type ReadLineCell,
} from './billReadModel';
import type { BillFieldKey } from './billFieldKeys';

interface Props {
  bill: BillDetail | null;
  loading?: boolean;
  /** Field highlighted from the other pane (form). */
  activeField?: BillFieldKey | null;
  /** Click a cell → select its field (drives cross-highlight). */
  onFieldSelect?: (key: BillFieldKey | null) => void;
}

const LINE_GRID =
  'grid grid-cols-[minmax(0,1fr)_64px_96px_104px] gap-px';

export function StructuredBillRead({ bill, loading, activeField, onFieldSelect }: Props) {
  if (loading && !bill) {
    return <Centered>Loading read…</Centered>;
  }
  if (!bill) {
    return <Centered>Select a bill to see the structured read.</Centered>;
  }

  const { sections, lines } = buildBillReadModel(bill);
  const select = (key: BillFieldKey) =>
    onFieldSelect?.(activeField === key ? null : key);

  return (
    <div className="h-full min-h-0 w-full overflow-y-auto bg-muted/20 p-3">
      <div className="mx-auto max-w-3xl space-y-3">
        {sections.map((section) => (
          <section
            key={section.id}
            className="overflow-hidden rounded-md border border-border bg-card"
          >
            <h3 className="border-b border-border bg-muted/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {section.title}
            </h3>
            <dl className="divide-y divide-border">
              {section.cells.map((cell) => (
                <ReadRow
                  key={cell.fieldKey}
                  cell={cell}
                  active={activeField === cell.fieldKey}
                  onSelect={() => select(cell.fieldKey)}
                />
              ))}
            </dl>
          </section>
        ))}

        <section className="overflow-hidden rounded-md border border-border bg-card">
          <h3 className="border-b border-border bg-muted/50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Line items ({lines.length})
          </h3>
          <div
            className={
              LINE_GRID +
              ' bg-muted/30 px-1.5 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground'
            }
          >
            <span>Description</span>
            <span className="text-right">Qty</span>
            <span className="text-right">Unit</span>
            <span className="text-right">Total</span>
          </div>
          {lines.length === 0 ? (
            <p className="px-3 py-2 text-xs italic text-muted-foreground">
              No line items extracted.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {lines.map((row) => (
                <div key={row.idx} className={LINE_GRID + ' px-1.5'}>
                  {row.cells.map((cell) => (
                    <LineCell
                      key={cell.fieldKey}
                      cell={cell}
                      active={activeField === cell.fieldKey}
                      onSelect={() => select(cell.fieldKey)}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </section>

        {bill.extraction_confidence != null && (
          <p className="px-1 text-[11px] text-muted-foreground">
            AI extraction confidence: {bill.extraction_confidence}
          </p>
        )}
      </div>
    </div>
  );
}

function ReadRow({
  cell,
  active,
  onSelect,
}: {
  cell: ReadCell;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      data-bill-read-field={cell.fieldKey}
      onClick={onSelect}
      className={[
        'flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors',
        active
          ? 'bg-primary/10 ring-1 ring-inset ring-primary/40'
          : 'hover:bg-accent/50',
      ].join(' ')}
    >
      <dt className="shrink-0 text-muted-foreground">{cell.label}</dt>
      <dd
        className={[
          'min-w-0 truncate text-foreground',
          cell.numeric ? 'tabular-nums' : '',
        ].join(' ')}
      >
        {cell.value}
      </dd>
    </button>
  );
}

function LineCell({
  cell,
  active,
  onSelect,
}: {
  cell: ReadLineCell;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      data-bill-read-field={cell.fieldKey}
      onClick={onSelect}
      title={cell.value}
      className={[
        'truncate px-1.5 py-1 text-left text-xs transition-colors',
        cell.numeric ? 'text-right tabular-nums' : '',
        active ? 'bg-primary/10 ring-1 ring-inset ring-primary/40' : 'hover:bg-accent/50',
      ].join(' ')}
    >
      {cell.value}
    </button>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-muted/20 p-6 text-center text-sm text-muted-foreground">
      <div>{children}</div>
    </div>
  );
}
